import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";import Landing from "@/pages/Landing";
import { trackPageview } from "@/lib/analytics";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import Disclosure from "@/pages/Disclosure";
import MemberHome from "@/pages/MemberHome";
import Scenario from "@/pages/Scenario";
import MemberHistory from "@/pages/MemberHistory";
import Certificates from "@/pages/Certificates";
import AccountSettings from "@/pages/AccountSettings";
import AgencyOverview from "@/pages/AgencyOverview";
import AgencyRoster from "@/pages/AgencyRoster";
import AgencyCompletion from "@/pages/AgencyCompletion";
import AgencyEngagement from "@/pages/AgencyEngagement";
import AgencySosList from "@/pages/AgencySosList";
import AgencyCriticalIncident from "@/pages/AgencyCriticalIncident";
import AgencyClinicalReview from "@/pages/AgencyClinicalReview";
import AgencyProductionQueue from "@/pages/AgencyProductionQueue";
import AgencyDemandSignals from "@/pages/AgencyDemandSignals";
import AgencyEngagementModule from "@/pages/AgencyEngagementModule";
import AgencyLeads from "@/pages/AgencyLeads";
import PlatformDashboard from "@/pages/PlatformDashboard";
import DemoScenario from "@/pages/DemoScenario";
import DemoSampleReport from "@/pages/DemoSampleReport";
import TrialSignIn from "@/pages/TrialSignIn";
import MagicLinkVerify from "@/pages/MagicLinkVerify";
import PlatformIntakes from "@/pages/PlatformIntakes";
import PlatformUsers from "@/pages/PlatformUsers";
import PlatformHmde from "@/pages/PlatformHmde";
import PlatformBuffer from "@/pages/PlatformBuffer";
import PlatformDemos from "@/pages/PlatformDemos";
import PlatformPersonas from "@/pages/PlatformPersonas";
import PlatformSpeaking from "@/pages/PlatformSpeaking";
import PlatformAnalytics from "@/pages/PlatformAnalytics";
import Speaking from "@/pages/Speaking";
import DemoImpersonationBanner from "@/components/DemoImpersonationBanner";

const RequireAuth = ({ children, role }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  if (!user.role) return <Navigate to="/role" replace />;
  if (user.role === "service_member" && !user.disclosure_acknowledged_at) {
    return <Navigate to="/disclosure" replace />;
  }
  if (role) {
    const allowed = Array.isArray(role) ? role.includes(user.role) : user.role === role;
    if (!allowed) {
      const dest = user.role === "platform_admin" ? "/platform"
                 : user.role === "demo_admin" ? "/admin"
                 : user.role === "agency_admin" ? "/agency"
                 : "/app";
      return <Navigate to={dest} replace />;
    }
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  React.useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/speaking" element={<Speaking />} />
      <Route path="/platform/speaking" element={<RequireAuth role="platform_admin"><PlatformSpeaking /></RequireAuth>} />
      <Route path="/platform/analytics" element={<RequireAuth role="platform_admin"><PlatformAnalytics /></RequireAuth>} />
      <Route path="/trial" element={<TrialSignIn mode="trial" />} />
      <Route path="/start-trial" element={<TrialSignIn mode="trial" />} />
      <Route path="/trial/:vertical" element={<TrialSignIn mode="trial" />} />
      <Route path="/signin" element={<TrialSignIn mode="signin" />} />
      <Route path="/demo" element={<DemoScenario />} />
      <Route path="/demo/sample-report" element={<DemoSampleReport />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/verify" element={<MagicLinkVerify />} />
      <Route path="/role" element={<RequireAuthOnly><RoleSelect /></RequireAuthOnly>} />
      <Route path="/disclosure" element={<RequireAuthOnly><Disclosure /></RequireAuthOnly>} />

      {/* Service member */}
      <Route path="/app" element={<RequireAuth role="service_member"><MemberHome /></RequireAuth>} />
      <Route path="/app/scenario/:moduleType" element={<RequireAuth role="service_member"><Scenario /></RequireAuth>} />
      <Route path="/app/history" element={<RequireAuth role="service_member"><MemberHistory /></RequireAuth>} />
      <Route path="/app/certificates" element={<RequireAuth role="service_member"><Certificates /></RequireAuth>} />
      <Route path="/app/settings" element={<RequireAuth role="service_member"><AccountSettings /></RequireAuth>} />

      {/* Agency admin */}
      <Route path="/agency" element={<RequireAuth role="agency_admin"><AgencyOverview /></RequireAuth>} />
      <Route path="/agency/roster" element={<RequireAuth role="agency_admin"><AgencyRoster /></RequireAuth>} />
      <Route path="/agency/completion" element={<RequireAuth role="agency_admin"><AgencyCompletion /></RequireAuth>} />
      <Route path="/agency/engagement" element={<RequireAuth role="agency_admin"><AgencyEngagement /></RequireAuth>} />
      <Route path="/agency/engagement/module/:module" element={<RequireAuth role="agency_admin"><AgencyEngagementModule /></RequireAuth>} />
      <Route path="/agency/sos" element={<RequireAuth role="agency_admin"><AgencySosList /></RequireAuth>} />
      <Route path="/agency/critical-incident" element={<RequireAuth role="agency_admin"><AgencyCriticalIncident /></RequireAuth>} />
      <Route path="/agency/clinical-review" element={<RequireAuth role="agency_admin"><AgencyClinicalReview /></RequireAuth>} />
      <Route path="/agency/production-queue" element={<RequireAuth role="agency_admin"><AgencyProductionQueue /></RequireAuth>} />
      <Route path="/agency/demand-signals" element={<RequireAuth role="agency_admin"><AgencyDemandSignals /></RequireAuth>} />
      <Route path="/agency/leads" element={<RequireAuth role="agency_admin"><AgencyLeads /></RequireAuth>} />

      {/* Platform admin (Haruki internal staff) */}
      <Route path="/platform" element={<RequireAuth role="platform_admin"><PlatformDashboard /></RequireAuth>} />
      <Route path="/platform/intakes" element={<RequireAuth role="platform_admin"><PlatformIntakes /></RequireAuth>} />
      <Route path="/platform/users" element={<RequireAuth role={["platform_admin", "demo_admin"]}><PlatformUsers /></RequireAuth>} />
      <Route path="/platform/hmde" element={<RequireAuth role="platform_admin"><PlatformHmde /></RequireAuth>} />
      <Route path="/platform/buffer" element={<RequireAuth role="platform_admin"><PlatformBuffer /></RequireAuth>} />
      <Route path="/platform/demos" element={<RequireAuth role="platform_admin"><PlatformDemos /></RequireAuth>} />
      <Route path="/platform/personas" element={<RequireAuth role="platform_admin"><PlatformPersonas /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth role={["platform_admin", "demo_admin"]}><PlatformUsers /></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const RequireAuthOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="dark min-h-screen bg-background" />;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DemoImpersonationBanner />
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
